  /**
   * FACTURES.JS — LOGIMES Platform
   * Workflow complet: Liste → Détails → Validate → Sign → Submit DSS → PDF
   * Traçabilité intégrée par facture
   * Auteur: PFE 2026 — ESEN Manouba
   */

  import React, { useEffect, useState, useCallback } from "react";
  import { useNavigate } from "react-router-dom";

  const MIDDLEWARE_URL = process.env.REACT_APP_API_URL;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  };

  // ── Statut config ──
  const STATUT_CONFIG = {
    V:          { label: "Validée",      color: "#16a34a", bg: "#dcfce7", icon: "✅" },
    C:          { label: "Créée",        color: "#2563eb", bg: "#dbeafe", icon: "📝" },
    ACCEPTED:   { label: "Acceptée ANCE",color: "#16a34a", bg: "#dcfce7", icon: "🏛️" },
    REJECTED:   { label: "Rejetée",      color: "#dc2626", bg: "#fee2e2", icon: "❌" },
    PENDING:    { label: "En attente",   color: "#d97706", bg: "#fef3c7", icon: "⏳" },
    SIGNED:     { label: "Signée",       color: "#7c3aed", bg: "#ede9fe", icon: "🔏" },
    SUBMITTED:  { label: "Soumise",      color: "#0891b2", bg: "#cffafe", icon: "📤" },
    default:    { label: "Inconnu",      color: "#64748b", bg: "#f1f5f9", icon: "❓" },
  };

  const getStatut = (s) => STATUT_CONFIG[s] || STATUT_CONFIG.default;

  // ── NAV items ──
  const NAV_ITEMS = [
    { icon: "🏠", label: "Accueil",     path: "/home" },
    { icon: "👥", label: "Clients",     path: "/clients" },
    { icon: "🧾", label: "Factures",    path: "/factures", active: true },
    { icon: "📊", label: "Statistiques",path: "/statistics" },
    { icon: "🔍", label: "Traçabilité", path: "/tracabilite" },
    { icon: "👤", label: "Mon Profil",  path: "/profile" },
  ];

  // ═══════════════════════════════════════════════════
  function Factures() {
    const navigate = useNavigate();

    const [factures,      setFactures]      = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [search,        setSearch]        = useState("");
    const [filterStatut,  setFilterStatut]  = useState("all");
    const [toast,         setToast]         = useState(null);
    const [actionLoading, setActionLoading] = useState({});

    // Modals
    const [detailModal,   setDetailModal]   = useState(null); // facture sélectionnée
    const [teifModal,     setTeifModal]     = useState(null);
    const [traceModal,    setTraceModal]    = useState(null);
    const [traceData,     setTraceData]     = useState(null);
    const [traceLoading,  setTraceLoading]  = useState(false);
    const [detailData,    setDetailData]    = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const userRole = getCookie("role") || "user";
    const userName = getCookie("name") || "Utilisateur";

    // ── Toast ──
    const showToast = (msg, type = "success") => {
      setToast({ msg, type });
      setTimeout(() => setToast(null), 4000);
    };

    // ── Auth headers ──
    const getHeaders = () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${getCookie("token")}`,
    });

    // ── Fetch factures ──
    const fetchFactures = useCallback(async () => {
      setLoading(true);
      try {
        const societeId = getCookie("societe_id");
  const url = societeId 
    ? `${MIDDLEWARE_URL}/factures?societe_id=${societeId}`
    : `${MIDDLEWARE_URL}/factures`;
  const res = await fetch(url, { headers: getHeaders() });
        const data = await res.json();
        setFactures(data.factures || data || []);
      } catch {
        showToast("Erreur chargement factures", "error");
      }
      setLoading(false);
    }, []);

    useEffect(() => { fetchFactures(); }, [fetchFactures]);

    // ── Actions workflow ──
    const doAction = async (numFacture, action, label) => {
      setActionLoading(prev => ({ ...prev, [`${numFacture}_${action}`]: true }));
      try {
        const res = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/${action}`, {
          method: "POST",
          headers: getHeaders(),
        });
        const data = await res.json();
        if (res.ok) {
          showToast(`✓ ${label} — Facture #${numFacture}`);
          fetchFactures();
          if (detailModal?.NumFacture === numFacture) {
            setDetailModal(prev => ({ ...prev, _refreshed: Date.now() }));
          }
        } else {
          showToast(data.detail || `Erreur ${label}`, "error");
        }
      } catch {
        showToast("Erreur réseau", "error");
      }
      setActionLoading(prev => ({ ...prev, [`${numFacture}_${action}`]: false }));
    };

    // ── Download XML ──
    const downloadXML = async (numFacture) => {
      try {
        const res = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/xml`, { headers: getHeaders() });
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `facture_${numFacture}.xml`; a.click();
        window.URL.revokeObjectURL(url);
        showToast(`XML téléchargé — #${numFacture}`);
      } catch {
        showToast("Erreur téléchargement XML", "error");
      }
    };

    // ── Download PDF ──
  const downloadPDF = async (numFacture) => {
    try {
      const societeId = getCookie("societe_id");
      const url = `${MIDDLEWARE_URL}/factures/${numFacture}/pdf${societeId ? `?societe_id=${societeId}` : ""}`;
      
      const res = await fetch(url, { 
        headers: getHeaders(),
        credentials: "include"
      });
      
      if (!res.ok) throw new Error("Erreur serveur");
      
      const blob = await res.blob();
      
      // تحقق الـ blob صح
      if (blob.size === 0) throw new Error("PDF vide");
      
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `facture_${numFacture}_TEIF.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      showToast(`✅ PDF téléchargé — #${numFacture}`);
    } catch (err) {
      showToast(`Erreur PDF: ${err.message}`, "error");
    }
  };

    // ── View TEIF ──
    const viewTeif = async (numFacture) => {
      setTeifModal({ num: numFacture, data: null, loading: true });
      try {
        const res = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/teif`, { headers: getHeaders() });
        const data = await res.json();
        setTeifModal({ num: numFacture, data, loading: false });
      } catch {
        setTeifModal({ num: numFacture, data: { error: "Erreur" }, loading: false });
      }
    };

    // ── View Traçabilité ──
    const viewTrace = async (numFacture) => {
      setTraceModal(numFacture);
      setTraceLoading(true);
      setTraceData(null);
      try {
        const [statusRes, historyRes] = await Promise.all([
          fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/status`, { headers: getHeaders() }),
          fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/history`, { headers: getHeaders() }),
        ]);
        const status  = await statusRes.json();
        const history = await historyRes.json();
        setTraceData({ status, history: history.history || history || [] });
      } catch {
        setTraceData({ error: "Erreur traçabilité" });
      }
      setTraceLoading(false);
    };

    const openDetail = async (f) => {
  setDetailModal(f);
  setDetailData(null);
  setDetailLoading(true);
  try {
    const societeId = getCookie("societe_id");
    const url = `${MIDDLEWARE_URL}/factures/${f.NumFacture}/detail${societeId ? `?societe_id=${societeId}` : ""}`;
    const res  = await fetch(url, { headers: getHeaders() });
    const data = await res.json();
    setDetailData(data);
  } catch {
    setDetailData({ error: "Erreur chargement détail" });
  }
  setDetailLoading(false);
};

    // ── Logout ──
    const handleLogout = () => {
      ["token","role","name"].forEach(k =>
        document.cookie = `${k}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
      );
      navigate("/");
    };

    // ── Filtrage ──
    const filtered = factures.filter(f => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        String(f.NumFacture).includes(q) ||
        String(f.NumFactureComptable || "").toLowerCase().includes(q) ||
        String(f.CodeClient || "").toLowerCase().includes(q);
      const matchStatut = filterStatut === "all" || f.Statut === filterStatut;
      return matchSearch && matchStatut;
    });

    // ── Stats rapides ──
    const stats = {
      total:    factures.length,
      validees: factures.filter(f => f.Statut === "V").length,
      creees:   factures.filter(f => f.Statut === "C").length,
      autres:   factures.filter(f => f.Statut !== "V" && f.Statut !== "C").length,
    };

    const isLoading = (num, action) => actionLoading[`${num}_${action}`];

    return (
      <div style={S.root}>

        {/* ══ SIDEBAR ══ */}
        <aside style={S.sidebar}>
          <div style={S.sidebarLogo} onClick={() => navigate("/home")}>
            <div style={S.logoIcon}>⚡</div>
            <span style={S.logoText}>LOGIMES</span>
          </div>
          <nav style={S.sidebarNav}>
            {NAV_ITEMS.map(item => (
              <div key={item.label}
                style={{ ...S.navItem, ...(item.active ? S.navItemActive : {}) }}
                onClick={() => navigate(item.path)}>
                <span style={S.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </nav>
          <div style={S.sidebarBottom}>
            <div style={S.userInfo}>
              <div style={S.userAvatar}>{userName.charAt(0).toUpperCase()}</div>
              <div>
                <div style={S.userName}>{userName}</div>
                <div style={S.userRole}>
                  {userRole === "super_admin" ? "👑 Super Admin" : userRole === "admin" ? "🔧 Admin" : "👤 Utilisateur"}
                </div>
              </div>
            </div>
            <button style={S.logoutBtn} onClick={handleLogout} title="Déconnexion">🚪</button>
          </div>
        </aside>

        {/* ══ MAIN ══ */}
        <main style={S.main}>

          {/* TOPBAR */}
          <header style={S.topbar}>
            <div>
              <h1 style={S.pageTitle}>🧾 Factures électroniques</h1>
              <p style={S.pageSubtitle}>Middleware TEIF — Workflow complet ANCE</p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.refreshBtn} onClick={fetchFactures}>🔄 Actualiser</button>
              <button style={S.createBtn} onClick={() => navigate("/facture/create")}>✨ Créer facture</button>
            </div>
          </header>

          <div style={S.content}>

            {/* ── STATS ── */}
            <div style={S.statsRow}>
              {[
                { icon: "🧾", label: "Total",    value: stats.total,    color: "#6c63ff" },
                { icon: "✅", label: "Validées",  value: stats.validees, color: "#16a34a" },
                { icon: "📝", label: "Créées",    value: stats.creees,   color: "#2563eb" },
                { icon: "📊", label: "Autres",    value: stats.autres,   color: "#d97706" },
              ].map(s => (
                <div key={s.label} style={S.statCard}>
                  <div style={{ fontSize: "1.6rem" }}>{s.icon}</div>
                  <div style={{ ...S.statValue, color: s.color }}>{s.value}</div>
                  <div style={S.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* ── TOOLBAR ── */}
            <div style={S.toolbar}>
              <div style={S.searchWrap}>
                <span>🔍</span>
                <input
                  style={S.searchInput}
                  placeholder="Rechercher par N° facture, client..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && <span style={{ cursor: "pointer", color: "#94a3b8" }} onClick={() => setSearch("")}>✕</span>}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["all", "C", "V"].map(s => (
                  <button key={s}
                    style={{ ...S.filterBtn, ...(filterStatut === s ? S.filterBtnActive : {}) }}
                    onClick={() => setFilterStatut(s)}>
                    {s === "all" ? "Toutes" : s === "V" ? "✅ Validées" : "📝 Créées"}
                  </button>
                ))}
              </div>
            </div>

            {/* ── RÉSULTAT INFO ── */}
            <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              {filtered.length} facture{filtered.length !== 1 ? "s" : ""} trouvée{filtered.length !== 1 ? "s" : ""}
            </div>

            {/* ── TABLE ── */}
            {loading ? (
              <div style={S.loadingBox}>
                <div style={S.spinner} />
                <p style={{ color: "#94a3b8", marginTop: 12 }}>Chargement des factures...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={S.emptyBox}>
                <div style={{ fontSize: "3rem" }}>🔍</div>
                <p style={{ color: "#94a3b8", marginTop: 8 }}>Aucune facture trouvée</p>
              </div>
            ) : (
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr style={S.thead}>
                      <th style={S.th}>N° Facture</th>
                      <th style={S.th}>N° Comptable</th>
                      <th style={S.th}>Client</th>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>HT</th>
                      <th style={S.th}>TTC</th>
                      <th style={S.th}>Statut</th>
                      <th style={S.th}>Workflow</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((f, idx) => {
                      const st = getStatut(f.Statut);
                      return (
                        <tr key={idx}
                          style={{ ...S.tr, ...(idx % 2 === 0 ? {} : S.trAlt) }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8f7ff"}
                          onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafafa"}>

                          <td style={S.td}>
                            <span style={S.numBadge} onClick={() => openDetail(f)}>
                              #{f.NumFacture}
                            </span>
                          </td>
                          <td style={S.td}><span style={{ color: "#64748b", fontSize: "0.82rem" }}>{f.NumFactureComptable || "—"}</span></td>
                          <td style={S.td}><span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.84rem" }}>{f.CodeClient || "—"}</span></td>
                          <td style={S.td}><span style={{ color: "#64748b", fontSize: "0.8rem" }}>{f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}</span></td>
                          <td style={S.td}><span style={{ fontSize: "0.82rem" }}>{f.MontantTotalHT != null ? Number(f.MontantTotalHT).toFixed(3) + " " + (f.DeviseAbrege || "TND") : "—"}</span></td>
                          <td style={S.td}><strong style={{ color: "#1a1a2e", fontSize: "0.84rem" }}>{f.MontantTotalTTC != null ? `${Number(f.MontantTotalTTC).toFixed(3)} ${f.DeviseAbrege || "TND"}` : f.MontantTotalFett != null ? `${Number(f.MontantTotalFett).toFixed(3)} TND` : "—"}</strong></td>
                          <td style={S.td}>
                            <span style={{ ...S.badge, color: st.color, background: st.bg }}>
                              {st.icon} {st.label}
                            </span>
                          </td>

                          {/* ── WORKFLOW BUTTONS ── */}
                          <td style={S.td}>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              <button style={S.wfBtn}
                                onClick={() => doAction(f.NumFacture, "validate", "Validation")}
                                disabled={isLoading(f.NumFacture, "validate")}
                                title="Valider XML">
                                {isLoading(f.NumFacture, "validate") ? "..." : "✅ Valid."}
                              </button>
                              <button style={{ ...S.wfBtn, ...S.wfBtnPurple }}
                                onClick={() => doAction(f.NumFacture, "sign", "Signature")}
                                disabled={isLoading(f.NumFacture, "sign")}
                                title="Signer">
                                {isLoading(f.NumFacture, "sign") ? "..." : "🔏 Sign."}
                              </button>
                              <button style={{ ...S.wfBtn, ...S.wfBtnBlue }}
                                onClick={() => doAction(f.NumFacture, "submit-dss", "Soumission ANCE")}
                                disabled={isLoading(f.NumFacture, "submit-dss")}
                                title="Soumettre à l'ANCE">
                                {isLoading(f.NumFacture, "submit-dss") ? "..." : "📤 DSS"}
                              </button>
                            </div>
                          </td>

                          {/* ── ACTIONS ── */}
                          <td style={S.td}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button style={S.actionBtn} onClick={() => downloadXML(f.NumFacture)} title="Télécharger XML">⬇️ XML</button>
                              <button style={{ ...S.actionBtn, ...S.actionBtnPdf }} onClick={() => downloadPDF(f.NumfactAffich || f.NumFacture)} title="Télécharger PDF">📄 PDF</button>
                              <button style={{ ...S.actionBtn, ...S.actionBtnTeif }} onClick={() => viewTeif(f.NumFacture)} title="Voir TEIF JSON">🔍</button>
                              <button style={{ ...S.actionBtn, ...S.actionBtnTrace }} onClick={() => viewTrace(f.NumFacture)} title="Traçabilité">📡</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>

        {/* ══ MODAL DÉTAIL ══ */}
        {detailModal && (
  <div style={S.overlay} onClick={() => { setDetailModal(null); setDetailData(null); }}>
    <div style={{ ...S.modal, maxWidth: 820 }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHeader}>
        <div>
          <h3 style={S.modalTitle}>🧾 Facture Électronique</h3>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#6c63ff", marginTop: 2 }}>
            #{detailModal.NumFacture}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...S.badge, color: getStatut(detailModal.Statut).color, background: getStatut(detailModal.Statut).bg, fontSize: "0.78rem" }}>
            {getStatut(detailModal.Statut).icon} {getStatut(detailModal.Statut).label}
          </span>
          <button style={S.modalClose} onClick={() => { setDetailModal(null); setDetailData(null); }}>✕</button>
        </div>
      </div>

      <div style={{ ...S.modalBody, maxHeight: "80vh", overflowY: "auto" }}>

        {/* ── EN-TÊTE ── */}
        <div style={S.detailGrid}>
          {[
            ["N° Facture",   detailModal.NumFacture],
            ["N° Comptable", detailModal.NumFactureComptable],
            ["Code Client",  detailModal.CodeClient],
            ["Date",         detailModal.DateFacture ? new Date(detailModal.DateFacture).toLocaleDateString("fr-FR") : "—"],
            ["Montant HT",   detailModal.MontantTotalHT != null ? Number(detailModal.MontantTotalHT).toFixed(3) + " TND" : "—"],
            ["TVA",          detailModal.MontantTotalTVA != null ? Number(detailModal.MontantTotalTVA).toFixed(3) + " TND" : "—"],
            ["Montant TTC",  detailModal.MontantTotalFett != null ? Number(detailModal.MontantTotalFett).toFixed(3) + " TND" : "—"],
            ["Statut",       getStatut(detailModal.Statut).icon + " " + getStatut(detailModal.Statut).label],
          ].map(([k, v]) => (
            <div key={k} style={S.detailRow}>
              <span style={S.detailKey}>{k}</span>
              <span style={S.detailVal}>{v || "—"}</span>
            </div>
          ))}
        </div>

        {/* ── LIGNES DETFACT ── */}
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            📋 Lignes de la facture
          </p>

          {detailLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <div style={S.spinner} />
            </div>
          ) : detailData?.error ? (
            <p style={{ color: "#dc2626", fontSize: "0.82rem" }}>❌ {detailData.error}</p>
          ) : detailData?.lignes?.length > 0 ? (
            <div style={{ overflowX: "auto", borderRadius: 10, border: "1.5px solid #e8e8f0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem", tableLayout: "auto" }}>
              <thead>
                <tr style={{ background: "#f8f7ff" }}>
                  {["Désignation", "Qté", "P.U HT", "Remise", "TVA", "Montant HT", "Montant TTC"].map(h => (
                    <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontWeight: 700, color: "#6c63ff", fontSize: "0.68rem", textTransform: "uppercase", borderBottom: "1.5px solid #e8e8f0", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detailData.lignes.map((ligne, i) => (
                  <tr key={i} className="ligne-row" style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f0f0f8" }}>
                    <td style={{ padding: "9px 12px", fontWeight: 600, color: "#1a1a2e", maxWidth: 200 }}>
                      {ligne.DesignationArticle || ligne.ReferenceArticle || "—"}
                    </td>
                    <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                      {ligne.QuantiteCommandee != null ? Number(ligne.QuantiteCommandee).toFixed(3) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                      {ligne.PrixUnitaire != null ? Number(ligne.PrixUnitaire).toFixed(3) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                      {ligne.Remise != null ? Number(ligne.Remise).toFixed(2) + "%" : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                      {ligne.TVA != null ? Number(ligne.TVA).toFixed(2) + "%" : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", fontWeight: 700, color: "#1a1a2e", textAlign: "right" }}>
                      {ligne.PrixHTRemise != null ? Number(ligne.PrixHTRemise).toFixed(3) + " " + (ligne.DeviseAbrege || "TND") : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", fontWeight: 700, color: "#6c63ff", textAlign: "right" }}>
                      {ligne.PrixTTC != null ? Number(ligne.PrixTTC).toFixed(3) + " " + (ligne.DeviseAbrege || "TND") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f0efff", borderTop: "2px solid #c7d2fe" }}>
                  <td colSpan={6} style={{ padding: "10px 12px", fontWeight: 700, color: "#6c63ff", fontSize: "0.8rem" }}>
                    TOTAL — {detailData.total_lignes} ligne{detailData.total_lignes !== 1 ? "s" : ""}
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 800, color: "#6c63ff", textAlign: "right" }}>
                    {detailModal.MontantTotalHT != null ? Number(detailModal.MontantTotalHT).toFixed(3) + " TND" : "—"}
                  </td>
                </tr>
              </tfoot>
              </table>
            </div>
          ) : detailData && detailData.lignes?.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: "0.82rem", textAlign: "center", padding: "16px 0" }}>
              Aucune ligne de détail disponible
            </p>
          ) : null}
        </div>

        {/* ── PARCOURS TEIF ── */}
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Parcours TEIF
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {[
              { icon: "⚡", label: "DAS ERP",       done: true },
              { icon: "📋", label: "Format TEIF",   done: detailModal.Statut === "V" || detailModal.Statut === "SIGNED" || detailModal.Statut === "SUBMITTED" || detailModal.Statut === "ACCEPTED" },
              { icon: "🔏", label: "Signature",     done: detailModal.Statut === "SIGNED" || detailModal.Statut === "SUBMITTED" || detailModal.Statut === "ACCEPTED" },
              { icon: "🏛️", label: "Soumette ANCE", done: detailModal.Statut === "SUBMITTED" || detailModal.Statut === "ACCEPTED" },
              { icon: "📡", label: "TTN",            done: detailModal.Statut === "ACCEPTED" },
              { icon: "🗄️", label: "Archivage",     done: detailModal.Statut === "ACCEPTED" },
            ].map((step, i, arr) => (
              <React.Fragment key={i}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: step.done ? "#6c63ff" : "#f0efff", border: `2px solid ${step.done ? "#6c63ff" : "#c7d2fe"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>
                    {step.icon}
                  </div>
                  <span style={{ fontSize: "0.6rem", color: step.done ? "#6c63ff" : "#94a3b8", fontWeight: 600, textAlign: "center", maxWidth: 60 }}>{step.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: step.done ? "#6c63ff" : "#e8e8f0", minWidth: 20, marginBottom: 16 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── ACTIONS ── */}
        <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={S.modalWfBtn} onClick={() => doAction(detailModal.NumFacture, "validate", "Validation")}>✅ Valider</button>
          <button style={{ ...S.modalWfBtn, background: "#ede9fe", color: "#7c3aed" }} onClick={() => doAction(detailModal.NumFacture, "sign", "Signature")}>🔏 Signer</button>
          <button style={{ ...S.modalWfBtn, background: "#dbeafe", color: "#2563eb" }} onClick={() => doAction(detailModal.NumFacture, "submit-dss", "Soumission")}>📤 Submit DSS</button>
          <button style={{ ...S.modalWfBtn, background: "#fee2e2", color: "#dc2626" }} onClick={() => downloadPDF(detailModal.NumfactAffich || detailModal.NumFacture)}>📄 PDF</button>
          <button style={{ ...S.modalWfBtn, background: "#f0fdf4", color: "#16a34a" }} onClick={() => downloadXML(detailModal.NumFacture)}>⬇️ XML</button>
          <button style={{ ...S.modalWfBtn, background: "#f0f9ff", color: "#0891b2" }} onClick={() => { setDetailModal(null); setDetailData(null); viewTrace(detailModal.NumFacture); }}>📡 Traçabilité</button>
        </div>
      </div>
    </div>
  </div>
)}

        {/* ══ MODAL TEIF ══ */}
        {teifModal && (
          <div style={S.overlay} onClick={() => setTeifModal(null)}>
            <div style={{ ...S.modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <h3 style={S.modalTitle}>🔍 TEIF JSON — Facture #{teifModal.num}</h3>
                <button style={S.modalClose} onClick={() => setTeifModal(null)}>✕</button>
              </div>
              <div style={{ ...S.modalBody, maxHeight: 500, overflowY: "auto" }}>
                {teifModal.loading ? (
                  <div style={{ textAlign: "center", padding: 40 }}><div style={S.spinner} /></div>
                ) : (
                  <pre style={S.jsonPre}>{JSON.stringify(teifModal.data, null, 2)}</pre>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ MODAL TRAÇABILITÉ ══ */}
        {traceModal && (
          <div style={S.overlay} onClick={() => { setTraceModal(null); setTraceData(null); }}>
            <div style={{ ...S.modal, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <h3 style={S.modalTitle}>📡 Traçabilité — Facture #{traceModal}</h3>
                <button style={S.modalClose} onClick={() => { setTraceModal(null); setTraceData(null); }}>✕</button>
              </div>
              <div style={{ ...S.modalBody, maxHeight: 500, overflowY: "auto" }}>
                {traceLoading ? (
                  <div style={{ textAlign: "center", padding: 40 }}><div style={S.spinner} /></div>
                ) : traceData?.error ? (
                  <p style={{ color: "#dc2626", textAlign: "center" }}>❌ {traceData.error}</p>
                ) : traceData ? (
                  <div>
                    {/* Statut actuel */}
                    <div style={S.traceStatus}>
                      <span style={{ fontSize: "1.4rem" }}>{getStatut(traceData.status?.statut || traceData.status?.status).icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, color: "#1a1a2e" }}>Statut actuel</div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{traceData.status?.statut || traceData.status?.status || "—"}</div>
                      </div>
                    </div>
                    {/* Historique */}
                    <p style={{ fontSize: "0.8rem", fontWeight: 700, color: "#64748b", margin: "16px 0 8px" }}>HISTORIQUE</p>
                    {(traceData.history || []).length === 0 ? (
                      <p style={{ color: "#94a3b8", fontSize: "0.82rem" }}>Aucun historique disponible</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {traceData.history.map((h, i) => (
                          <div key={i} style={S.traceItem}>
                            <div style={S.traceDot} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: "0.84rem", color: "#1a1a2e" }}>{h.statut || h.status || h.action || "—"}</div>
                              <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{h.date || h.timestamp || h.created_at || "—"}</div>
                              {h.message && <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>{h.message}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* ══ TOAST ══ */}
        {toast && (
          <div style={{ ...S.toast, ...(toast.type === "error" ? S.toastError : S.toastSuccess) }}>
            {toast.type === "error" ? "❌" : "✅"} {toast.msg}
          </div>
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Outfit', sans-serif; }
          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: #f1f1f1; }
          ::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 3px; }
          @keyframes slideIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes toastIn { from { opacity:0; transform:translateX(100px); } to { opacity:1; transform:translateX(0); } }
        `}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  const S = {
    root: { display: "flex", minHeight: "100vh", background: "#f5f6fa", fontFamily: "'Outfit', sans-serif" },

    // Sidebar
    sidebar: { width: 220, position: "fixed", top: 0, left: 0, bottom: 0, background: "#fff", borderRight: "1.5px solid #ededf5", display: "flex", flexDirection: "column", zIndex: 200, boxShadow: "4px 0 20px rgba(108,99,255,0.06)" },
    sidebarLogo: { display: "flex", alignItems: "center", gap: 10, padding: "20px 16px 12px", cursor: "pointer" },
    logoIcon: { fontSize: "1.3rem", background: "#6c63ff", width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    logoText: { fontSize: "1.05rem", fontWeight: 800, color: "#6c63ff" },
    sidebarNav: { flex: 1, overflowY: "auto", padding: "8px" },
    navItem: { display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, cursor: "pointer", fontSize: "0.84rem", fontWeight: 500, color: "#64748b", marginBottom: 2, borderLeft: "3px solid transparent", transition: "all 0.15s" },
    navItemActive: { background: "#6c63ff18", color: "#6c63ff", borderLeft: "3px solid #6c63ff", fontWeight: 700 },
    navIcon: { fontSize: "0.95rem", width: 20, textAlign: "center" },
    sidebarBottom: { borderTop: "1.5px solid #ededf5", padding: "10px", display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" },
    userInfo: { display: "flex", alignItems: "center", gap: 8, overflow: "hidden" },
    userAvatar: { width: 30, height: 30, borderRadius: "50%", background: "#6c63ff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.82rem", fontWeight: 700, flexShrink: 0 },
    userName: { fontSize: "0.78rem", fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 },
    userRole: { fontSize: "0.65rem", color: "#94a3b8", fontWeight: 600 },
    logoutBtn: { background: "#fff0f0", border: "none", borderRadius: 7, padding: "6px 8px", cursor: "pointer", fontSize: "0.95rem", flexShrink: 0 },

    // Main
    main: { marginLeft: 220, flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" },
    topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 32px", background: "#fff", borderBottom: "1.5px solid #ededf5", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(108,99,255,0.05)" },
    pageTitle: { fontSize: "1.2rem", fontWeight: 800, color: "#1a1a2e" },
    pageSubtitle: { fontSize: "0.76rem", color: "#94a3b8", marginTop: 2 },
    refreshBtn: { background: "#f0efff", color: "#6c63ff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: "0.82rem", fontFamily: "'Outfit',sans-serif" },
    createBtn: { background: "#6c63ff", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: "0.82rem", fontFamily: "'Outfit',sans-serif" },
    content: { padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 },

    // Stats
    statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 },
    statCard: { background: "#fff", borderRadius: 13, padding: "18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, border: "1.5px solid #e8e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", textAlign: "center" },
    statValue: { fontSize: "1.8rem", fontWeight: 800 },
    statLabel: { fontSize: "0.72rem", color: "#94a3b8", fontWeight: 500 },

    // Toolbar
    toolbar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
    searchWrap: { flex: 1, minWidth: 240, display: "flex", alignItems: "center", background: "#fff", border: "1.5px solid #e8e8f0", borderRadius: 10, padding: "0 12px", gap: 8 },
    searchInput: { flex: 1, border: "none", outline: "none", fontSize: "0.88rem", padding: "10px 0", fontFamily: "'Outfit',sans-serif", background: "transparent", color: "#1a1a2e" },
    filterBtn: { background: "#f5f6fa", border: "1.5px solid #e8e8f0", color: "#64748b", padding: "8px 14px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif" },
    filterBtnActive: { background: "#6c63ff", color: "#fff", borderColor: "#6c63ff" },

    // Table
    tableWrap: { background: "#fff", borderRadius: 14, border: "1.5px solid #e8e8f0", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.05)", animation: "slideIn 0.3s ease" },
    table: { width: "100%", borderCollapse: "collapse" },
    thead: { background: "linear-gradient(135deg, #f8f7ff, #f0efff)" },
    th: { padding: "12px 14px", textAlign: "left", fontSize: "0.68rem", fontWeight: 700, color: "#6c63ff", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1.5px solid #e8e8f0", whiteSpace: "nowrap" },
    tr: { background: "#fff", transition: "background 0.15s" },
    trAlt: { background: "#fafafa" },
    td: { padding: "11px 14px", borderBottom: "1px solid #f0f0f8", verticalAlign: "middle" },
    numBadge: { background: "#f0efff", color: "#6c63ff", padding: "3px 10px", borderRadius: 6, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" },
    badge: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap" },

    // Workflow buttons
    wfBtn: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", padding: "4px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap" },
    wfBtnPurple: { background: "#ede9fe", color: "#7c3aed", border: "1px solid #c4b5fd" },
    wfBtnBlue: { background: "#dbeafe", color: "#2563eb", border: "1px solid #93c5fd" },

    // Action buttons
    actionBtn: { background: "#f5f6fa", color: "#64748b", border: "1px solid #e8e8f0", padding: "4px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap" },
    actionBtnPdf: { background: "#fff0f0", color: "#dc2626", border: "1px solid #fecaca" },
    actionBtnTeif: { background: "#f0efff", color: "#6c63ff", border: "1px solid #c7d2fe" },
    actionBtnTrace: { background: "#f0f9ff", color: "#0891b2", border: "1px solid #bae6fd" },

    // Loading / Empty
    loadingBox: { background: "#fff", borderRadius: 14, padding: "60px", display: "flex", flexDirection: "column", alignItems: "center", border: "1.5px solid #e8e8f0" },
    spinner: { width: 36, height: 36, border: "3px solid #f0efff", borderTop: "3px solid #6c63ff", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
    emptyBox: { background: "#fff", borderRadius: 14, padding: "60px", display: "flex", flexDirection: "column", alignItems: "center", border: "1.5px solid #e8e8f0" },

    // Modals
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" },
    modal: { background: "#fff", borderRadius: 18, width: "90%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", animation: "slideIn 0.2s ease", overflow: "hidden" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1.5px solid #f0f0f8" },
    modalTitle: { fontSize: "1rem", fontWeight: 800, color: "#1a1a2e" },
    modalClose: { background: "#f5f6fa", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: "0.8rem" },
    modalBody: { padding: "20px 24px" },
    modalWfBtn: { background: "#f0fdf4", color: "#16a34a", border: "none", padding: "8px 14px", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: "0.82rem", fontFamily: "'Outfit',sans-serif" },

    // Detail grid
    detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    detailRow: { background: "#f8f7ff", borderRadius: 8, padding: "10px 14px" },
    detailKey: { display: "block", fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 },
    detailVal: { display: "block", fontSize: "0.88rem", fontWeight: 700, color: "#1a1a2e" },

    // JSON pre
    jsonPre: { background: "#1a1a2e", color: "#e2e8f0", padding: 16, borderRadius: 10, fontSize: "0.75rem", lineHeight: 1.6, overflowX: "auto", fontFamily: "monospace" },

    // Traçabilité
    traceStatus: { display: "flex", alignItems: "center", gap: 12, background: "#f8f7ff", borderRadius: 10, padding: "14px 16px" },
    traceItem: { display: "flex", alignItems: "flex-start", gap: 10, background: "#f8f7ff", borderRadius: 8, padding: "10px 14px" },
    traceDot: { width: 8, height: 8, borderRadius: "50%", background: "#6c63ff", flexShrink: 0, marginTop: 6 },

    // Toast
    toast: { position: "fixed", bottom: 24, right: 24, padding: "14px 22px", borderRadius: 12, fontWeight: 600, fontSize: "0.88rem", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", zIndex: 2000, animation: "toastIn 0.3s ease", maxWidth: 380 },
    toastSuccess: { background: "#1a1a2e", color: "#fff" },
    toastError: { background: "#dc2626", color: "#fff" },
  };

  export default Factures;